import React, { useCallback, useMemo } from 'react';

import { css } from '@emotion/css';
import { Theme } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import {
  ColumnDef,
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';

import { PatientListData } from '@/hooks/usePatientsListData.ts';
import { useStyles } from '@/hooks/useStyles.ts';
import { PatientListItem } from '@/models/PatientSummaryModel.ts';
import { Profession } from '@/models/ProfessionModel.ts';
import { PractitionerUser } from '@/models/UserModel.ts';
import {
  DiabetesTypeColumn,
  DiabetesTypeHeader,
} from '@/pages/patients/patients-list/DiabetesTypeColumn.tsx';
import {
  DoctorMessagesColumn,
  MessagesHeader,
  NurseMessagesColumn,
} from '@/pages/patients/patients-list/MessagesColumn.tsx';
import {
  DoctorNoticeColumn,
  NoticeHeader,
  NurseNoticeColumn,
} from '@/pages/patients/patients-list/NoticeColumn.tsx';
import { PatientListMenuItems } from '@/pages/patients/patients-list/PatientListMenu.tsx';
import {
  DoctorPatientNameColumn,
  NursePatientNameColumn,
  PatientNameHeader,
} from '@/pages/patients/patients-list/PatientNameColumn.tsx';
import {
  ReferentDoctorColumn,
  ReferentDoctorHeader,
} from '@/pages/patients/patients-list/ReferentDoctorColumn.tsx';
import {
  TelemonitoringColumn,
  TelemonitoringHeader,
} from '@/pages/patients/patients-list/TelemonitoringColumn.tsx';
import { Queries } from '@/queries/Queries.ts';
import { MuiTable } from '@/uiKit/Table.tsx';

export type PatientListTableProps = {
  selectedItem: PatientListMenuItems;
  patientListData: PatientListData;
  me: PractitionerUser;
};

const columnHelper = createColumnHelper<PatientListItem>();

const REFERENT_DOCTOR_COLUMN_SIZE = 15;
const DIABETES_TYPE_COLUMN_SIZE = 15;
const TELEMONITORING_COLUMN_SIZE = 15;
const NOTICES_COLUMN_SIZE = 15;
const MESSAGES_COLUMN_SIZE = 15;

const columnDefinitions = {
  default: {
    size: 0,
    minSize: 0,
    maxSize: 100,
  },
  patientName: (qualification: Profession) =>
    columnHelper.accessor('familyName', {
      cell:
        qualification === 'doctor'
          ? DoctorPatientNameColumn
          : NursePatientNameColumn,
      header: PatientNameHeader,
    }),
  diabetes: columnHelper.accessor('diabetesType', {
    cell: DiabetesTypeColumn,
    header: DiabetesTypeHeader,
    size: DIABETES_TYPE_COLUMN_SIZE,
  }),
  telemonitoring: columnHelper.accessor('tags', {
    cell: TelemonitoringColumn,
    header: TelemonitoringHeader,
    size: TELEMONITORING_COLUMN_SIZE,
  }),
  notices: (qualification: Profession) => {
    return columnHelper.accessor('hasNurseNotice', {
      cell: qualification === 'doctor' ? DoctorNoticeColumn : NurseNoticeColumn,
      header: NoticeHeader,
      size: NOTICES_COLUMN_SIZE,
    });
  },
  messages: (qualification: Profession) => {
    return columnHelper.accessor('unreadMessages', {
      cell:
        qualification === 'doctor' ? DoctorMessagesColumn : NurseMessagesColumn,
      header: MessagesHeader,
      size: MESSAGES_COLUMN_SIZE,
    });
  },
  referentDoctor: columnHelper.accessor('doctor.id', {
    cell: ReferentDoctorColumn,
    header: ReferentDoctorHeader,
    size: REFERENT_DOCTOR_COLUMN_SIZE,
  }),
};

const makeColumnArray = (
  ...columns: (ColumnDef<PatientListItem, never> | undefined)[]
) => {
  return columns.filter(c => c !== undefined);
};

const selectDistinct = <T,>(array: T[]) => {
  return [...new Set(array)];
};

export const PatientListTable: React.FC<PatientListTableProps> = ({
  selectedItem,
  patientListData,
  me,
}) => {
  const styles = useStyles(makeStyles);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const hasMultipleDoctors =
    selectDistinct(
      patientListData.all
        .map(patient => patient.doctor?.id)
        .filter(d => d !== undefined),
    ).length > 1;

  const columns = useMemo(() => {
    const patientNameColumn = columnDefinitions.patientName(me.qualification);
    const messagesColumn = columnDefinitions.messages(me.qualification);
    const noticesColumn = columnDefinitions.notices(me.qualification);
    const referentDoctorColumn =
      hasMultipleDoctors && me.qualification === 'nurse'
        ? columnDefinitions.referentDoctor
        : undefined;
    const referentDoctorColumnSize =
      hasMultipleDoctors && me.qualification === 'nurse'
        ? REFERENT_DOCTOR_COLUMN_SIZE
        : 0;

    switch (selectedItem) {
      case 'monitored':
        patientNameColumn.size =
          100 -
          NOTICES_COLUMN_SIZE -
          MESSAGES_COLUMN_SIZE -
          referentDoctorColumnSize;
        return makeColumnArray(
          patientNameColumn,
          referentDoctorColumn,
          messagesColumn,
          noticesColumn,
        );
      case 'gestational':
        patientNameColumn.size =
          100 -
          DIABETES_TYPE_COLUMN_SIZE -
          NOTICES_COLUMN_SIZE -
          MESSAGES_COLUMN_SIZE -
          referentDoctorColumnSize;
        return makeColumnArray(
          patientNameColumn,
          columnDefinitions.diabetes,
          referentDoctorColumn,
          messagesColumn,
          noticesColumn,
        );
      case 'all':
        patientNameColumn.size =
          100 -
          DIABETES_TYPE_COLUMN_SIZE -
          TELEMONITORING_COLUMN_SIZE -
          NOTICES_COLUMN_SIZE -
          MESSAGES_COLUMN_SIZE -
          referentDoctorColumnSize;
        return makeColumnArray(
          patientNameColumn,
          columnDefinitions.telemonitoring,
          columnDefinitions.diabetes,
          referentDoctorColumn,
          messagesColumn,
          noticesColumn,
        );
    }
  }, [selectedItem, me.qualification]);

  const table = useReactTable<PatientListItem>({
    data: patientListData[selectedItem],
    defaultColumn: columnDefinitions.default,
    columns: columns as ColumnDef<PatientListItem>[],
    getCoreRowModel: getCoreRowModel(),
  });

  const handleClick = useCallback((patient: PatientListItem) => {
    Queries.messaging.useMessages.invalidate(queryClient, {
      patientId: patient.id,
    });
    navigate({
      to: `/patients/${patient.id}`,
    });
  }, []);

  return (
    <MuiTable
      id="patient-list-table"
      table={table}
      handleRowClick={handleClick}
      rowClassName="patient-list-row"
      cellClassName={styles.cell}
    />
  );
};

const makeStyles = (theme: Theme) => ({
  container: css`
    width: 100%;
    height: 100%;
  `,
  cell: css`
    height: ${theme.spacing(26)};
    padding: ${theme.spacing(2, 0)};
  `,
});
